import { Bell, HelpCircle, LogOut, Moon, Sun, Monitor, User, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTheme } from "@/components/theme-provider";
import { BrandWordmark } from "@/components/brand-wordmark";
import { TERMS } from "@tasty/shared";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface HeaderProps {
  onSignOut?: () => void;
  userInitials?: string;
  restaurantName?: string;
  onMobileMenuClick?: () => void;
}

export function Header({ 
  onSignOut, 
  userInitials = "RD", 
  restaurantName,
  onMobileMenuClick 
}: HeaderProps) {
  const { theme, setTheme } = useTheme();

  return (
    <header className="fixed top-0 right-0 left-0 z-50 h-14 bg-background border-b border-border flex items-center px-4 md:px-6 justify-between shadow-sm">
      {/* Left: Brand & Mobile Menu */}
      <div className="flex items-center gap-2 md:gap-4">
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden h-9 w-9 text-muted-foreground"
          onClick={onMobileMenuClick}
          aria-label="Menü öffnen"
        >
          <Menu className="w-5 h-5" />
        </Button>
        
        <div className="flex items-center gap-2">
          <BrandWordmark className="font-black text-lg md:text-xl tracking-tighter" />
        </div>
      </div>

      {/* Right: Meta Actions */}
      <div className="flex items-center gap-2 md:gap-4">
        {/* Help & Support (Desktop only) */}
        <div className="hidden lg:flex items-center gap-4 text-xs font-bold text-muted-foreground mr-2">
          <button className="hover:text-foreground transition-colors flex items-center gap-1.5 uppercase tracking-widest">
            <HelpCircle className="w-3.5 h-3.5" />
            Support
          </button>
        </div>

        {/* Theme Toggle */}
        <div className="flex items-center bg-muted/30 p-1 rounded-lg border border-border">
          <Button 
            variant={theme === "light" ? "secondary" : "ghost"} 
            size="icon" 
            className="h-7 w-7 rounded-md"
            onClick={() => setTheme("light")}
            title="Hell"
          >
            <Sun className="w-3.5 h-3.5" />
          </Button>
          <Button 
            variant={theme === "system" ? "secondary" : "ghost"} 
            size="icon" 
            className="h-7 w-7 rounded-md hidden sm:flex"
            onClick={() => setTheme("system")}
            title="System"
          >
            <Monitor className="w-3.5 h-3.5" />
          </Button>
          <Button 
            variant={theme === "dark" ? "secondary" : "ghost"} 
            size="icon" 
            className="h-7 w-7 rounded-md"
            onClick={() => setTheme("dark")}
            title="Dunkel"
          >
            <Moon className="w-3.5 h-3.5" />
          </Button>
        </div>

        {/* User / Restaurant Profile */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div className="flex items-center gap-2 md:gap-3 pl-2 md:pl-4 border-l border-border cursor-pointer group">
              <div className="flex flex-col items-end hidden sm:flex">
                <span className="text-xs font-black truncate max-w-[120px] leading-none mb-1">
                  {restaurantName || TERMS.business}
                </span>
                <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest leading-none">
                  Admin
                </span>
              </div>
              <div className="h-8 w-8 md:h-9 md:w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-black shadow-inner group-hover:scale-105 transition-transform">
                {userInitials}
              </div>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 mt-2 rounded-xl shadow-2xl">
            <DropdownMenuLabel className="font-bold">
              <div className="flex flex-col">
                <span>{restaurantName || TERMS.business}</span>
                <span className="text-[10px] text-muted-foreground font-normal">Administrator</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="cursor-pointer font-medium py-2">
              <User className="mr-2 h-4 w-4" /> Profil
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={onSignOut}
              className="cursor-pointer font-bold text-destructive focus:text-destructive py-2"
            >
              <LogOut className="mr-2 h-4 w-4" /> Abmelden
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
